<script setup lang="ts">
import { formatEpoch, formatMoneyInt } from '@/utils/format'

import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { PayOrderRow } from '@/api/types/domain'
import {
  deletePayOrder,
  fetchPayOrders,
  patchPayOrderAccount,
  patchPayOrderWithdrawStatus,
} from '@/api/modules/payorders'

const loading = ref(false)
const pageUi = ref(1)
const size = ref(10)
const rows = ref<PayOrderRow[]>([])
const total = ref(0)

const q = reactive({ search: '' })

function withdrawStatusLabel(s?: number | null) {
  if (s === 1 || s === 0) return '待处理'
  if (s === -1) return '提现审核不通过'
  if (s === 2) return '处理中'
  if (s === 3) return '处理成功'
  if (s === 4) return '处理失败'
  return s == null ? '—' : `状态(${s})`
}

async function load() {
  loading.value = true
  try {
    const { data } = await fetchPayOrders(Math.max(0, pageUi.value - 1), size.value, {
      search: q.search.trim() || undefined,
    })
    rows.value = data.content ?? []
    total.value = data.totalElements ?? 0
  } finally {
    loading.value = false
  }
}

function search() {
  pageUi.value = 1
  load()
}

function sz(v: number) {
  size.value = v
  pageUi.value = 1
  load()
}

function pg() {
  load()
}

async function editAccount(row: PayOrderRow) {
  try {
    const { value } = await ElMessageBox.prompt(
      `新收款账户（卡号）· 订单 ${row.orderNum ?? row.id}`,
      '修改卡号',
      {
        inputValue: row.account ?? '',
        inputPlaceholder: '至少 11 位',
        inputValidator: (v) => {
          if (!v || v.trim().length < 11) return '卡号不能为空且至少 11 位'
          return true
        },
      },
    )
    const { data } = await patchPayOrderAccount(row.id, value.trim())
    if (data.status !== 1) {
      ElMessage.error(data.msg ?? '失败')
      return
    }
    ElMessage.success(data.msg ?? '已保存')
    load()
  } catch (e) {
    if (e !== 'cancel') {
      /* interceptor */
    }
  }
}

async function setWithdrawStatus(row: PayOrderRow, status: number, confirmText: string) {
  try {
    await ElMessageBox.confirm(confirmText, '状态', { type: 'warning' })
    const { data } = await patchPayOrderWithdrawStatus(row.id, status)
    if (data.status !== 1) {
      ElMessage.error(data.msg ?? '失败')
      return
    }
    ElMessage.success(data.msg ?? '已更新')
    load()
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

async function removeRow(row: PayOrderRow) {
  try {
    await ElMessageBox.confirm(`确认删除提现单"${row.orderNum ?? row.id}"？`, '确认删除', {
      type: 'warning',
      confirmButtonText: '删除',
    })
    await deletePayOrder(row.id)
    ElMessage.success('已删除')
    load()
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

onMounted(load)
</script>

<template>
  <PageChrome
    title="提现管理"
    description="同一检索框可同时按手机号或提现订单号模糊（OR）；银行、账户、金额、状态与旧版一致。status：1 待处理，3 处理成功，4 处理失败，-1 审核不通过。"
  >
    <div class="ep-filter-panel">
      <el-form inline class="pw-filter">
        <el-form-item label="手机/订单号">
          <el-input v-model="q.search" clearable placeholder="手机号或提现订单号 · 模糊" style="width: 288px" />
        </el-form-item>
        <el-form-item>
          <el-space>
            <el-button type="primary" @click="search">查询</el-button>
            <el-button :loading="loading" @click="load">刷新</el-button>
          </el-space>
        </el-form-item>
      </el-form>
    </div>

    <el-card shadow="hover" class="ep-table-scope pw-card">
      <el-table v-loading="loading" :data="rows" stripe border empty-text="暂无" size="default">
        <el-table-column prop="id" label="ID" width="72" align="center" />
        <el-table-column prop="orderNum" label="订单号" min-width="132" show-overflow-tooltip />
        <el-table-column prop="user" label="手机号" width="118" show-overflow-tooltip />
        <el-table-column prop="name" label="客户姓名" width="108" show-overflow-tooltip />
        <el-table-column prop="banks" label="收款银行" min-width="112" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="txt-warn">{{ (row as PayOrderRow).banks || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="account" label="收款账户" min-width="132" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="txt-warn">{{ (row as PayOrderRow).account || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="金额" width="100" align="right">
          <template #default="{ row }">
            <span class="txt-warn">
              {{
                formatMoneyInt((row as PayOrderRow).money)
              }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="172" align="center">
          <template #default="{ row }">{{ formatEpoch((row as PayOrderRow).addtime ?? undefined) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="120" align="center">
          <template #default="{ row }">
            <span class="txt-warn">{{ withdrawStatusLabel((row as PayOrderRow).status) }}</span>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="298">
          <template #default="{ row }">
            <el-button link type="primary" @click="editAccount(row as PayOrderRow)">修改卡号</el-button>
            <el-button link type="danger" @click="removeRow(row as PayOrderRow)">删除</el-button>
            <br />
            <el-button link type="primary" @click="setWithdrawStatus(row as PayOrderRow, -1, '设为审核不通过（-1）？')">
              审核失败
            </el-button>
            <el-button link type="success" @click="setWithdrawStatus(row as PayOrderRow, 3, '设为处理成功（3）？')">
              处理成功
            </el-button>
            <el-button link type="warning" @click="setWithdrawStatus(row as PayOrderRow, 4, '设为处理失败（4）？')">
              处理失败
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="ep-pager-footer pager">
        <el-pagination
          v-model:current-page="pageUi"
          v-model:page-size="size"
          :total="total"
          :page-sizes="[10, 15, 30, 50]"
          layout="sizes,prev,pager,next,total"
          background
          @current-change="pg"
          @size-change="sz"
        />
      </div>
    </el-card>
  </PageChrome>
</template>

<style scoped lang="scss">
.pw-filter {
  margin-bottom: 0;
}

.pw-card {
  border-radius: var(--ep-radius-lg) !important;
}

.pager {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.txt-warn {
  color: #b45309;
  font-weight: 500;
}
</style>
